import { DEFAULT_SESSION_LIFETIME_MS } from "@/types"
import { isRemoved } from "@/utils/removal"
import type { Session } from "@/types"

/*
 * When a session stops being usable.
 *
 * Two ways out, and they are not the same thing: the facilitator marks it
 * complete, or it simply runs out of time. Completion is a write someone makes;
 * expiry is a moment passing, so it is DERIVED here rather than stored — no
 * client is awake to write an `expired` status at the moment it happens, and a
 * session that expired overnight would still read as active until somebody
 * opened it.
 */

/** The wall-clock moment a session created now would expire. */
export function expiryFromNow(lifetimeMs: number = DEFAULT_SESSION_LIFETIME_MS): number {
  return Date.now() + lifetimeMs
}

/**
 * A session with no `expiresAt` never expires. That is the honest reading of a
 * missing field: sessions written before expiry existed had no deadline, and
 * inventing one would close a workshop that is still in the room.
 */
export function isExpired(session: Session | null, now: number = Date.now()): boolean {
  if (!session || session.expiresAt === null) return false
  if (session.status === "completed") return false
  return session.expiresAt <= now
}

/**
 * Whether the share link still admits people.
 *
 * Both `pending` and `active` accept guests: someone arriving late joins a
 * session already in its second round, and refusing them would mean the room
 * has to restart to add one person.
 *
 * An ARCHIVED or DELETED session admits nobody, and this is the client half of
 * what makes that true — `firestore.rules` folds the same test into `isLive`, so
 * every write beneath the session is refused whatever the screen believes. The
 * product asks for a live session to be disposable; a share link that kept
 * working after it was deleted would make that a lie.
 */
export function isJoinable(session: Session | null, now: number = Date.now()): boolean {
  if (!session) return false
  if (session.status === "completed") return false
  if (isRemoved(session)) return false
  return !isExpired(session, now)
}

/**
 * The five-value vocabulary every list, row and card renders.
 *
 * `SessionStatus` has three values and expiry is derived, so any screen that
 * asked `status === "active"` on its own would read a workshop that ended last
 * week as still running. This is the one place that resolves both facts
 * together — go through it rather than re-deriving, and the mistake cannot come
 * back.
 *
 * `paused` means a session the room is not actively working — either the
 * facilitator parked the whole thing for a follow-up call (`pausedAt`, set by
 * Save & quit and cleared the moment the room moves again), or they stopped the
 * round clock mid-round (`timer.status`, what `resumeTimer` un-does). Both read
 * as "stopped, resumable" in a list, which is what the state is for.
 */
export type SessionState = "draft" | "live" | "paused" | "complete" | "expired" | "removed"

export function sessionState(session: Session | null, now: number = Date.now()): SessionState {
  if (!session) return "draft"
  if (session.status === "completed") return "complete"
  if (isRemoved(session)) return "removed"
  if (isExpired(session, now)) return "expired"
  if (session.status === "pending") return "draft"
  if (session.pausedAt !== null) return "paused"
  return session.timer.status === "paused" ? "paused" : "live"
}
